import { useState, useContext, useEffect, useMemo } from 'react'
import { PassContext } from '../pass-context'
import { flatten } from "flat"
import _ from "lodash"


import { TextField, Typography, Button, FormControlLabel, FormLabel, RadioGroup, Radio, Tooltip } from '@mui/material'
import EastRoundedIcon from '@mui/icons-material/EastRounded'
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined'

import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'

import dataSelector from './data-selector'
import InstanceDataSelector from '/utils/instance-data-selector'
import currencies from '/utils/currencies.json'
import PopupShell from '../popup-shell'

const PopupDialogText = ({ open, close, title }) => {

  const { objectData, instanceData, setObjectData, scannerInstanceData, openInstanceDataManager } = useContext(PassContext)

  const [newObjectData, setNewObjectData] = useState({ loyaltyPoints: { balance: { int: 0 }, label: "" } })
  const [sourcePaths, setSourcePaths] = useState([])
  const [dataSource, setDataSource] = useState("object")

  const dataChanged = useMemo(() => {
    if (open) {
      if (!_.isEqual(objectData, newObjectData)) return true
    }

    return false
  }, [newObjectData])

  // Runs on "open" state change or if template / instanceData change 
  // Ensures we have the current data rather than something left behind from previous form 
  useEffect(() => {
    try {
      if (open) {
        setNewObjectData(_.cloneDeep(objectData))

        let objectLabel = _.get(objectData, "loyaltyPoints.label.defaultValue.value")
        let source = objectLabel?.match(/^{{.*}}$/) ? "instance" : "object"

        setDataSource(source)
      }
    }
    catch (err) {
      throw err
    }
  }, [open, objectData])

  // Refresh our template / instance data source paths if instance data changes
  useEffect(() => {
    setSourcePaths(Object.keys(flatten({ ...instanceData, __scanner_data__:{...scannerInstanceData} })).map(p => `{{${p}}}`).sort())
  }, [instanceData, scannerInstanceData])

  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Support Functions
  //////////////////////////////////////////////////////////////////////////////////////////////////////////


  const setLoyaltyPointsType = newType => {

    const oldType = Object.keys(newObjectData.loyaltyPoints.balance)[0]
    const valueKey = oldType === "money" ? ("loyaltyPoints.balance.money.micros") : ("loyaltyPoints.balance." + oldType)
    const oldValue = _.get(newObjectData, valueKey)

    setNewObjectData(prev => {
      let temp = { ...prev }

      if (newType === "money") {
        _.set(temp, "loyaltyPoints.balance.money.micros", oldValue)
        _.set(temp, "loyaltyPoints.balance.money.currencyCode", "GBP")
      } else {
        _.set(temp, ("loyaltyPoints.balance." + newType), oldValue)
      }

      _.unset(temp, ("loyaltyPoints.balance." + oldType))
      return temp
    })
  }

  const handleChangeDataSource = value => {
    setDataSource(value)
    setNewObjectData(prev => {
      let temp = { ...prev }
      _.set(temp, "loyaltyPoints.label", value)
      return temp
    })
  }

  const handleClose = (e, reason) => {
    if (reason && reason === "backdropClick")
      return
    close()
  }

  const saveFormData = () => {

    // Save content of our "new" template/instanceData back to state
    if (!_.isEqual(newObjectData, objectData)) setObjectData(newObjectData)

    close()
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Main Render
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  const loyaltyPointsType = Object.keys(newObjectData.loyaltyPoints.balance)[0]

  return (
    <PopupShell open={open} onClose={handleClose}>

      <div className="flex flex-col p-5 h-full">

        <div className="flex flex-row justify-between mt-5">
          <Typography color="secondary" variant="h6">{title}</Typography>
          <Tooltip title="You can press ESC on your keyboard to dismiss"><Button endIcon={<EastRoundedIcon />} onClick={handleClose} >cancel</Button></Tooltip>
        </div>

        <hr />

        <div className='mt-5 overflow-y-scroll flex flex-col gap-5 mb-[100px]'>


          <Typography variant="h5" sx={{ p: 2 }}>Label</Typography>

          <FormLabel id="data_type_label">Data Source</FormLabel>
          <RadioGroup
            row
            aria-labelledby="data_type_label"
            value={dataSource}
            onChange={e => handleChangeDataSource(e.target.value)}
          >
            <FormControlLabel value="object" control={<Radio />} label="Object (fixed for all passes)" />
            <FormControlLabel value="instance" control={<Radio />} label="Instance (unique for each pass)" />
          </RadioGroup>

          {dataSource === "object" && <DataSourceObject path="loyaltyPoints.label" newObjectData={newObjectData} setNewObjectData={setNewObjectData} />}
          {dataSource === "instance" && <DataSourceInstance path="loyaltyPoints.label" newObjectData={newObjectData} setNewObjectData={setNewObjectData} instanceData={instanceData} sourcePaths={sourcePaths} />}

          <Typography variant="h5" sx={{ p: 2 }}>Points</Typography>

          <FormLabel id="balance_type_label">Points Type</FormLabel>
          <RadioGroup
            aria-labelledby="balance_type_label"
            value={loyaltyPointsType}
            onChange={e => setLoyaltyPointsType(e.target.value)}
          >
            <FormControlLabel value="int" control={<Radio />} label="Numeric" />
            <FormControlLabel value="double" control={<Radio />} label="Decimal" />
            <FormControlLabel value="money" control={<Radio />} label="Money" />
            <FormControlLabel value="string" control={<Radio />} label="Text" />
          </RadioGroup>

          {loyaltyPointsType === "money" &&
            <>
              <Typography variant="h6" sx={{ p: 2 }}>Currency</Typography>
              <Select
                value={newObjectData.loyaltyPoints.balance.money.currencyCode}
                onChange={e => setNewObjectData("loyaltyPoints.balance.money.currencyCode", e.target.value)}
                className="popupEditorMenuItem"
              >
                {currencies.map(c => <MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>)}
              </Select>

              <Typography variant="h6" sx={{ p: 2 }}>Value</Typography>

              <DataSourceInstance path="loyaltyPoints.balance.money.micros" newObjectData={newObjectData} setNewObjectData={setNewObjectData} instanceData={instanceData} scannerInstanceData={scannerInstanceData} sourcePaths={sourcePaths} />
            </>
          }

          {loyaltyPointsType !== "money" &&
            <DataSourceInstance path={`loyaltyPoints.balance[${loyaltyPointsType}]`} newObjectData={newObjectData} setNewObjectData={setNewObjectData} instanceData={instanceData} scannerInstanceData={scannerInstanceData} sourcePaths={sourcePaths} />
          }
        </div>

        <div className='fixed bottom-5 w-[460px]'>
          <hr className='mb-5' />
          <div className="flex flex-row gap-5 justify-between">
            <Button startIcon={<DatasetOutlinedIcon />} onClick={openInstanceDataManager} >Edit sample data</Button>
            <Button variant="contained" disabled={!dataChanged} onClick={saveFormData}>Save</Button>
          </div>
        </div>

      </div>
    </PopupShell>
  )
}

export default PopupDialogText


//////////////////////////////////////////////////////////////////////////////////////////////////////////
// Controlled JSX (Must have values passes as paramets and not access them from the parent 
// - hence outside the main function). Otherwise they do not update when data changes!
//////////////////////////////////////////////////////////////////////////////////////////////////////////


const DataSourceObject = ({ path, newObjectData, setNewObjectData }) => {

  const objectDataValue = _.get(newObjectData, path)

  const changeHandler = value => {
    setNewObjectData(prev => {
      let nd = { ...prev }
      _.set(nd, path, value)
      return nd
    })
  }

  if (open) return (
    <TextField label="Object value"
      value={objectDataValue}
      slotProps={{ inputLabel: { shrink: true } }}
      type="search"
      onChange={e => { changeHandler(e.target.value) }}
    />
  )
}

const DataSourceInstance = ({ path, newObjectData, setNewObjectData, instanceData, scannerInstanceData, sourcePaths }) => {
  const templateValue = _.get(newObjectData, path)
  const instanceDataValue = dataSelector({ objectData: newObjectData, instanceData, scannerInstanceData, path })

  const changeHandler = value => {
    setNewObjectData(prev => {
      let nd = { ...prev }
      _.set(nd, path, value)
      return nd
    })
  }

  if (open) return (
    <>
      <InstanceDataSelector updateValue={changeHandler} sourcePaths={sourcePaths} value={templateValue} dataSelector={dataSelector} instanceData={instanceData} scannerInstanceData={scannerInstanceData} />

      <TextField label="Sample data"
        value={instanceDataValue}
        slotProps={{ inputLabel: { shrink: true } }}
        type="search"
        disabled={true}
        fullWidth
      />
    </>
  )
}
